import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { isApiError, versionTag, type ListMeta, type Query } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { PUBLIC_QUERY_SCOPE } from '@/lib/session'
import { isUploadError, useUploader, type Uploader } from '@/lib/upload'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'
import type { PreparedPhoto } from './photo-file'

/**
 * Planos de tratamento (docs/api/planos.md; regras em docs/areas/planos-de-tratamento.md, RN-PLA-01
 * a 40) e a sessão de fotos por QR (docs/api/fotos.md, função pública `photos`). Os tipos são os do
 * contrato; as telas só falam com a API por estes hooks (T25). Toda escrita no plano leva
 * `If-Match: "<version>"` (trava otimista, RN-PLA-24); a foto sobe em dois passos (RN-PLA-17).
 */

export type TreatmentPlanStatus = 'open' | 'closed' | 'archived'
export type TreatmentPlanSource = 'contract' | 'manual' | 'legacy'
export type TreatmentPlanFilter = 'open' | 'closed' | 'all' | 'pending' | 'archived'
export type TreatmentPlanSort = 'createdAt' | '-createdAt' | 'closedAt' | '-closedAt' | 'patient'
export type ImageConsent = 'yes' | 'no' | 'not_provided'
export type Stage = 'marking' | 'before' | 'immediately_after' | 'after'
export type LotSlot = 'vial' | 'box' | 'product_label'
/** A chave de `lotPhotos` para cada slot. */
export type LotSlotKey = 'vial' | 'box' | 'productLabel'
export type ProductUnit = 'ml' | 'U' | 'mg' | 'unit' | 'session'
export type PhotoSource = 'desktop' | 'gallery' | 'mobile'
export type PendingType =
  | 'treatment_plan_without_product'
  | 'treatment_plan_lot_pending'
  | 'treatment_plan_photos_incomplete'
export type EmailStatus = 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'not_sent'

export interface PersonRef {
  id: string
  name: string
}

export interface PendingItem {
  type: PendingType
  label: string
}

/** O item da lista (`GET /treatment-plans`). */
export interface TreatmentPlanRow {
  id: string
  version: number
  status: TreatmentPlanStatus
  createdAt: string
  closedAt: string | null
  archivedAt: string | null
  patient: PersonRef
  procedures: string[]
  practitioner: PersonRef
  totalAmountCents: number
  recordedStageCount: number
  pendingItem: PendingItem | null
  pendingItems: PendingItem[]
  /** Preenchida só quando o plano é de clínica irmã da rede (RT-06). */
  clinic: PersonRef | null
  /** Só no filtro `archived`. */
  archivedBy?: PersonRef | null
  archiveReason?: string | null
  previousStatus?: TreatmentPlanStatus | null
}

export interface TreatmentPlanListMeta extends ListMeta {
  counts: { open: number; closed: number; all: number; pending: number; archived?: number }
}

export interface Photo {
  id: string
  stage?: Stage
  position?: number
  slot?: LotSlot | 'expiry_date'
  productId?: string
  status: 'pending_upload' | 'uploaded'
  takenAt: string | null
  uploadedAt: string | null
  uploadedBy: PersonRef | null
  source: PhotoSource
  mimeType: string
  sizeBytes: number
  width: number | null
  height: number | null
  url: string | null
  urlExpiresAt: string | null
}

export interface ProductRegion {
  regionId: string | null
  name: string
  commonName: string | null
  label: string
  isCustom: boolean
}

export interface Region {
  id: string
  group: 'facial' | 'body' | 'clinic'
  name: string
  commonName: string | null
  label: string
  isCustom: boolean
}

export interface RegionGroup {
  group: 'facial' | 'body' | 'clinic'
  title: string
  total: number
  regions: Region[]
}

export interface LotPhotos {
  vial: Photo | null
  box: Photo | null
  productLabel: Photo | null
  /** O slot antigo "Prazo de Validade" do legado, só de leitura (P-BD-22). */
  expiryDate?: Photo | null
}

export interface Product {
  id: string
  version: number
  position: number
  catalogProductId: string | null
  name: string | null
  manufacturer: string | null
  applicationDate: string | null
  quantity: number | null
  unit: ProductUnit | null
  regions: ProductRegion[]
  lotNumber: string | null
  expiryDate: string | null
  anvisaRegistration: string | null
  isExpiredAtApplication: boolean
  lotPhotos: LotPhotos
  isLotPending: boolean
  createdAt: string
  createdBy: PersonRef | null
}

export interface PhotoSession {
  id: string
  target: 'stage' | 'product'
  stage: Stage | null
  productId: string | null
  /** Só na criação (`POST /treatment-plans/:id/qr`). */
  url?: string
  openedBy: PersonRef
  createdAt: string
  expiresAt: string
  revokedAt: string | null
  uploadedCount: number
  limit: number
}

export interface PlanEmail {
  recipient: 'patient' | 'clinic'
  address: string | null
  sentAt: string | null
  status: EmailStatus
}

export interface PlanWarning {
  code: 'documents_canceled'
  occurredAt: string
  message: string
}

export interface Evidence {
  protocolNumber: string
  /** SHA-256 dos bytes do PDF (o `ETag` do download). */
  hash: string
  /** SHA-256 do Markdown montado, impresso no bloco Registro do PDF (T41). */
  contentHash?: string
  occurredAt: string
  ip: string | null
  userAgent: string | null
  device: string | null
}

/** A ficha (`GET /treatment-plans/:id`). */
export interface TreatmentPlan {
  id: string
  version: number
  status: TreatmentPlanStatus
  source: TreatmentPlanSource
  appointmentId: string
  patient: { id: string; name: string; birthDate: string | null }
  ageAtProcedure: number | null
  isAgeEnteredManually: boolean
  imageConsent: ImageConsent
  practitioner: {
    id: string
    name: string
    profession: string | null
    council: string | null
    registrationNumber: string | null
  }
  isPractitionerEditable: boolean
  /** O estado do atendimento (`open` · `draft` · `generated` · `sent` · …): diz se há documentos. */
  appointmentState?: string
  procedures: { name: string; amountCents: number }[]
  totalAmountCents: number
  procedureDate: string | null
  procedureTime: string | null
  notes: string
  createdAt: string
  createdBy: PersonRef | null
  updatedAt: string
  closedAt: string | null
  closedBy: PersonRef | null
  evidence: Evidence | null
  pdf: { fileName: string; sizeBytes: number } | null
  emails: PlanEmail[]
  archivedAt: string | null
  archivedBy: PersonRef | null
  archiveReason: string | null
  previousStatus: TreatmentPlanStatus | null
  warnings: PlanWarning[]
  products: Product[]
  photos: Record<Stage, Photo[]>
  recordedStageCount: number
  pendingItems: PendingItem[]
  pendingItem: PendingItem | null
  photoSessions: PhotoSession[]
  clinic: PersonRef | null
  /** A leitura da etiqueta pela foto está disponível para esta clínica (P-PLA-16). */
  labelReadingAvailable?: boolean
}

/** O corpo de `PUT /treatment-plans/:id`: só o que muda (RN-PLA-05, RN-PLA-06). */
export interface TreatmentPlanInput {
  notes?: string
  ageAtProcedure?: number
  procedureDate?: string | null
  procedureTime?: string | null
  practitionerId?: string
}

export interface CreateTreatmentPlanInput {
  patientId: string
  procedureIds: string[]
  practitionerId: string
  procedureDate?: string
  procedureTime?: string
  isModelPatient?: boolean
  ageAtProcedure?: number
}

/** O corpo de `POST` e `PUT` do produto; `regions` substitui a lista inteira (RN-PLA-11). */
export interface ProductInput {
  catalogProductId?: string | null
  name?: string | null
  manufacturer?: string | null
  applicationDate?: string | null
  quantity?: number | null
  unit?: ProductUnit | null
  regions?: ({ regionId: string } | { name: string })[]
  lotNumber?: string | null
  expiryDate?: string | null
  anvisaRegistration?: string | null
}

export interface UploadTicket {
  id: string
  status: 'pending_upload'
  stage?: Stage
  position?: number
  slot?: LotSlot
  productId?: string
  upload: { url: string; method: 'PUT'; headers: Record<string, string>; expiresAt: string }
  /** O caminho do arquivo no armazenamento, devolvido no passo 1 para voltar na confirmação. */
  storagePath?: string
}

/**
 * O passo 2 (RN-PLA-17): `storagePath` é o do passo 1; `thumbnailDataUrl` é a miniatura feita no
 * aparelho (lado maior 480px, WebP ou JPEG, até 300 KB), opcional (T41).
 */
export interface ConfirmPhotoInput {
  source: 'desktop' | 'gallery'
  takenAt?: string
  width?: number
  height?: number
  storagePath?: string
  thumbnailDataUrl?: string
}

export interface StagePhotoConfirmed extends Photo {
  treatmentPlan: { recordedStageCount: number; pendingItems: PendingItem[] }
}

export interface LotPhotoConfirmed extends Photo {
  product: { version: number; isLotPending: boolean }
  treatmentPlan: { pendingItems: PendingItem[] }
}

export interface ProductDeleted {
  id: string
  archivedAt: string
  restorableUntil: string
  treatmentPlan: { version: number; pendingItems: PendingItem[] }
}

export interface RecentProduct {
  catalogProductId: string | null
  name: string
  manufacturer: string
  unit: ProductUnit | null
  lastUsedAt: string
}

export interface CatalogProduct {
  catalogProductId: string
  name: string
  manufacturer: string
  category: 'toxin' | 'filler' | 'biostimulator' | 'thread' | 'other'
  unit: ProductUnit | null
  anvisaRegistration: string | null
}

export interface ProductSearchResult {
  recentlyUsed: RecentProduct[]
  catalog: CatalogProduct[]
}

/** A leitura da etiqueta pela foto do lote (Proposta P-PLA-16). */
export interface LabelReading {
  suggestions: {
    name: string | null
    manufacturer: string | null
    lotNumber: string | null
    expiryDate: string | null
    anvisaRegistration: string | null
  }
  confidence: 'high' | 'medium' | 'low'
  photoIds: string[]
}

export interface SentEmail {
  recipient: 'patient'
  address: string
  status: EmailStatus
  queuedAt: string
}

// A sessão pública (docs/api/fotos.md) ----------------------------------------------------------

export interface PublicPhoto {
  id: string
  position: number
  takenAt: string | null
  thumbnailUrl: string | null
  urlExpiresAt: string | null
  isFromThisPhotoSession: boolean
}

export interface PublicSlot {
  isOccupied: boolean
  thumbnailUrl?: string | null
  urlExpiresAt?: string | null
}

export type PublicTarget =
  | {
      type: 'stage'
      stage: Stage
      title: string
      description: string
      uploadedCount: number
      limit: number
      photos: PublicPhoto[]
    }
  | {
      type: 'product'
      product: { label: string }
      slots: { vial: PublicSlot; box: PublicSlot; productLabel: PublicSlot }
    }

export interface PhotoSessionInfo {
  photoSessionId: string
  expiresAt: string
  treatmentPlan: { label: string; clinicName: string }
  target: PublicTarget
  photoSession: { uploadedCount: number; limit: number }
  version: number
}

export interface PublicUploadTicket {
  photoId: string
  stage?: Stage
  position?: number
  slot?: LotSlot
  productId?: string
  status: 'pending_upload'
  upload: UploadTicket['upload']
  storagePath?: string
}

export interface PublicPhotoConfirmed {
  photo: {
    id: string
    stage?: Stage
    position?: number
    slot?: LotSlot
    status: 'uploaded'
    takenAt: string | null
    uploadedAt: string
    source: 'mobile'
    thumbnailUrl: string | null
    urlExpiresAt: string | null
  }
  target: { uploadedCount: number; limit: number }
  photoSession: { uploadedCount: number; limit: number }
  version: number
}

/** O que `GET /photos/state` devolve quando algo mudou (RN-PLA-22). */
export interface PhotoSessionState {
  photoSessionId: string
  version: number
  expiresAt: string
  target: { type: 'stage' | 'product'; stage?: Stage; uploadedCount?: number; limit?: number }
  photoSession: { uploadedCount: number; limit: number }
  newPhotos: Photo[]
}

// Chaves e cache ---------------------------------------------------------------------------------

export interface TreatmentPlanListParams {
  filter: TreatmentPlanFilter
  from?: string
  to?: string
  search?: string
  patientId?: string
  sort?: TreatmentPlanSort
  page?: number
  perPage?: number
}

export const treatmentPlanKeys = {
  all: ['treatment-plans'] as const,
  lists: ['treatment-plans', 'list'] as const,
  list: (params: TreatmentPlanListParams) => ['treatment-plans', 'list', params] as const,
  detail: (id: string) => ['treatment-plans', 'detail', id] as const,
  regions: (search: string) => ['treatment-plans', 'regions', search] as const,
  productSearch: (search: string) => ['treatment-plans', 'product-search', search] as const,
}

/** O que muda quando um plano muda: as listas, a ficha da paciente e o dashboard. */
export function invalidateTreatmentPlans(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['patients'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ])
}

/** Troca a ficha no cache pela que a API devolveu (toda escrita devolve a ficha inteira). */
export function putPlanInCache(queryClient: QueryClient, plan: TreatmentPlan) {
  queryClient.setQueryData(treatmentPlanKeys.detail(plan.id), plan)
}

/** Ajusta a ficha no cache sem pedir de novo (uma foto que chegou, um produto que mudou). */
export function patchPlanInCache(
  queryClient: QueryClient,
  id: string,
  patch: (plan: TreatmentPlan) => TreatmentPlan,
) {
  queryClient.setQueryData<TreatmentPlan>(treatmentPlanKeys.detail(id), (current) =>
    current ? patch(current) : current,
  )
}

/** A ficha com um produto trocado pelo que a API devolveu. */
export function withProduct(plan: TreatmentPlan, product: Product): TreatmentPlan {
  const exists = plan.products.some((p) => p.id === product.id)
  return {
    ...plan,
    products: exists
      ? plan.products.map((p) => (p.id === product.id ? product : p))
      : [...plan.products, product].sort((a, b) => a.position - b.position),
  }
}

/** A ficha com uma foto de etapa a mais (ou a menos), e as pendências que a API recalculou. */
export function withStagePhoto(
  plan: TreatmentPlan,
  photo: Photo,
  summary?: { recordedStageCount: number; pendingItems: PendingItem[] },
): TreatmentPlan {
  const stage = photo.stage
  if (!stage) return plan
  const list = plan.photos[stage] ?? []
  const photos = {
    ...plan.photos,
    [stage]: list.some((p) => p.id === photo.id)
      ? list.map((p) => (p.id === photo.id ? photo : p))
      : [...list, photo].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  }
  const recordedStageCount =
    summary?.recordedStageCount ??
    (Object.values(photos) as Photo[][]).filter((items) => items.length > 0).length
  return {
    ...plan,
    photos,
    recordedStageCount,
    pendingItems: summary?.pendingItems ?? plan.pendingItems,
    pendingItem: (summary?.pendingItems ?? plan.pendingItems)[0] ?? null,
  }
}

export function withoutStagePhoto(
  plan: TreatmentPlan,
  stage: Stage,
  photoId: string,
  summary?: { recordedStageCount: number; pendingItems: PendingItem[] },
): TreatmentPlan {
  const photos = { ...plan.photos, [stage]: plan.photos[stage].filter((p) => p.id !== photoId) }
  return {
    ...plan,
    photos,
    recordedStageCount:
      summary?.recordedStageCount ??
      (Object.values(photos) as Photo[][]).filter((items) => items.length > 0).length,
    pendingItems: summary?.pendingItems ?? plan.pendingItems,
    pendingItem: (summary?.pendingItems ?? plan.pendingItems)[0] ?? null,
  }
}

/** A ficha com a foto de um slot do lote trocada (a anterior é arquivada pela API, RN-PLA-12). */
export function withLotPhoto(
  plan: TreatmentPlan,
  productId: string,
  slot: LotSlot,
  photo: Photo | null,
  product?: { version: number; isLotPending: boolean },
  summary?: { pendingItems: PendingItem[] },
): TreatmentPlan {
  const key = slotKey(slot)
  const products = plan.products.map((p) =>
    p.id === productId
      ? {
          ...p,
          lotPhotos: { ...p.lotPhotos, [key]: photo },
          version: product?.version ?? p.version,
          isLotPending: product?.isLotPending ?? p.isLotPending,
        }
      : p,
  )
  return {
    ...plan,
    products,
    pendingItems: summary?.pendingItems ?? plan.pendingItems,
    pendingItem: (summary?.pendingItems ?? plan.pendingItems)[0] ?? null,
  }
}

export function slotKey(slot: LotSlot): LotSlotKey {
  return slot === 'product_label' ? 'productLabel' : slot
}

/** O 409 da trava otimista: a versão atual e quem mudou (docs/api/planos.md, "Convenções"). */
export function conflictVersion(error: unknown): number | null {
  if (!isApiError(error) || error.code !== 'conflict') return null
  const current = error.details.currentVersion
  return typeof current === 'number' ? current : null
}

// Leituras ---------------------------------------------------------------------------------------

export function useTreatmentPlans(
  params: TreatmentPlanListParams,
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: treatmentPlanKeys.list(params),
    queryFn: () => {
      const query: Query = {
        filter: params.filter,
        from: params.from,
        to: params.to,
        search: params.search,
        patientId: params.patientId,
        sort: params.sort,
        page: params.page ?? 1,
        perPage: params.perPage ?? 10,
      }
      return api.list<TreatmentPlanRow, TreatmentPlanListMeta>('/treatment-plans', query)
    },
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  })
}

export function useTreatmentPlan(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: treatmentPlanKeys.detail(id ?? ''),
    queryFn: () => api.get<TreatmentPlan>(`/treatment-plans/${id}`),
    enabled: Boolean(id),
  })
}

export function useRegions(search: string) {
  const api = useApi()
  return useQuery({
    queryKey: treatmentPlanKeys.regions(search),
    queryFn: () =>
      api.get<{ groups: RegionGroup[] }>('/treatment-plans/regions', {
        search: search.length >= 2 ? search.slice(0, 60) : undefined,
      }),
    placeholderData: (previous) => previous,
  })
}

export function useProductSearch(search: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: treatmentPlanKeys.productSearch(search),
    queryFn: () =>
      api.get<ProductSearchResult>('/treatment-plans/products', {
        search: search.slice(0, 80) || undefined,
        limit: 10,
      }),
    enabled,
    placeholderData: (previous) => previous,
  })
}

/** Região personalizada da clínica ("+ Adicionar 'ponto nasal lateral'"); repetida devolve a existente. */
export function useCreateRegion() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => api.post<Region>('/treatment-plans/regions', { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['treatment-plans', 'regions'] }),
  })
}

// O plano ----------------------------------------------------------------------------------------

export function useCreateTreatmentPlan() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CreateTreatmentPlanInput) =>
      api.post<TreatmentPlan>('/treatment-plans', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: (plan) => {
      idempotency.reset()
      putPlanInCache(queryClient, plan)
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

/** O `PUT` das Informações gerais; o autosave chama direto pela função, com a versão da tela. */
export function useUpdateTreatmentPlan(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ input, version }: { input: TreatmentPlanInput; version: number }) =>
      api.put<TreatmentPlan>(`/treatment-plans/${id}`, input, {
        headers: { 'If-Match': versionTag(version) },
      }),
    onSuccess: (plan) => {
      putPlanInCache(queryClient, plan)
      void queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.lists })
    },
  })
}

export function useCloseTreatmentPlan(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: ({
      version,
      hasConfirmedIncompleteStages,
    }: {
      version: number
      hasConfirmedIncompleteStages?: boolean
    }) => {
      const body = hasConfirmedIncompleteStages ? { hasConfirmedIncompleteStages: true } : {}
      return api.post<TreatmentPlan>(`/treatment-plans/${id}/close`, body, {
        headers: { 'If-Match': versionTag(version) },
        idempotencyKey: idempotency.keyFor({ id, version }),
      })
    },
    onSuccess: (plan) => {
      idempotency.reset()
      putPlanInCache(queryClient, plan)
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useArchiveTreatmentPlan(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ version, reason }: { version: number; reason?: string }) =>
      api.post<TreatmentPlan>(`/treatment-plans/${id}/archive`, reason ? { reason } : {}, {
        headers: { 'If-Match': versionTag(version) },
      }),
    onSuccess: (plan) => {
      putPlanInCache(queryClient, plan)
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useRestoreTreatmentPlan(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ version }: { version: number }) =>
      api.post<TreatmentPlan>(
        `/treatment-plans/${id}/restore`,
        {},
        {
          headers: { 'If-Match': versionTag(version) },
        },
      ),
    onSuccess: (plan) => {
      putPlanInCache(queryClient, plan)
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

/** "Restaurar" direto da lista de arquivados (RN-PLA-33), onde cada linha tem o próprio id. */
export function useRestoreTreatmentPlanRow() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      api.post<TreatmentPlan>(
        `/treatment-plans/${id}/restore`,
        {},
        {
          headers: { 'If-Match': versionTag(version) },
        },
      ),
    onSuccess: (plan) => {
      putPlanInCache(queryClient, plan)
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useSendTreatmentPlanEmail(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<SentEmail>(`/treatment-plans/${id}/send-email`, { recipient: 'patient' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.detail(id) }),
  })
}

/** O PDF gerado no encerramento, sempre o mesmo arquivo (RN-PLA-27). */
export function useTreatmentPlanPdf() {
  const api = useApi()
  return useMutation({
    mutationFn: (id: string) => api.download(`/treatment-plans/${id}/pdf`),
  })
}

// Produtos ---------------------------------------------------------------------------------------

export function useAddProduct(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: ProductInput = {}) =>
      api.post<Product>(`/treatment-plans/${id}/products`, input, {
        idempotencyKey: idempotency.keyFor({ id, input, at: Date.now() }),
      }),
    onSuccess: (product) => {
      idempotency.reset()
      patchPlanInCache(queryClient, id, (plan) => withProduct(plan, product))
      // A versão do plano subiu; a ficha volta a ser pedida para a trava continuar certa.
      void queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.detail(id) })
    },
  })
}

export function useUpdateProduct(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      productId,
      version,
      input,
    }: {
      productId: string
      version: number
      input: ProductInput
    }) =>
      api.put<Product>(`/treatment-plans/${id}/products/${productId}`, input, {
        headers: { 'If-Match': versionTag(version) },
      }),
    onSuccess: (product) => {
      patchPlanInCache(queryClient, id, (plan) => withProduct(plan, product))
      void queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.detail(id) })
    },
  })
}

export function useDeleteProduct(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ productId, version }: { productId: string; version: number }) => {
      // O `DELETE` devolve corpo (a janela do Desfazer): lido como `GET` de um item não é possível
      // pelo cliente; a resposta que importa é o plano que a API recalculou, pedido em seguida.
      await api.delete(`/treatment-plans/${id}/products/${productId}`, undefined, {
        headers: { 'If-Match': versionTag(version) },
      })
      return productId
    },
    onSuccess: (productId) => {
      patchPlanInCache(queryClient, id, (plan) => ({
        ...plan,
        products: plan.products.filter((p) => p.id !== productId),
      }))
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useRestoreProduct(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (productId: string) =>
      api.post<Product>(`/treatment-plans/${id}/products/${productId}/restore`, {}),
    onSuccess: (product) => {
      patchPlanInCache(queryClient, id, (plan) => withProduct(plan, product))
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useLabelReading(id: string) {
  const api = useApi()
  return useMutation({
    mutationFn: (productId: string) =>
      api.post<LabelReading>(`/treatment-plans/${id}/products/${productId}/label-reading`),
  })
}

// Fotos (dois passos, RN-PLA-17) -----------------------------------------------------------------

/** O envio em dois passos: reserva, `PUT` do arquivo na URL assinada e confirmação. */
export interface ReservedUpload {
  id: string
  upload: UploadTicket['upload']
  storagePath?: string
}

export async function uploadInTwoSteps<T>(
  photo: PreparedPhoto,
  upload: Uploader,
  reserve: () => Promise<ReservedUpload>,
  confirm: (ticket: ReservedUpload) => Promise<T>,
): Promise<T> {
  const ticket = await reserve()
  await upload(
    { uploadUrl: ticket.upload.url, method: ticket.upload.method, headers: ticket.upload.headers },
    photo.blob,
  )
  return confirm(ticket)
}

/** O corpo da confirmação: as medidas, o `takenAt` quando há, o caminho do passo 1 e a miniatura. */
export function confirmBody(
  photo: PreparedPhoto,
  ticket: ReservedUpload,
): Omit<ConfirmPhotoInput, 'source'> {
  return {
    width: photo.width,
    height: photo.height,
    ...(photo.takenAt ? { takenAt: photo.takenAt } : {}),
    ...(ticket.storagePath ? { storagePath: ticket.storagePath } : {}),
    ...(photo.thumbnailDataUrl ? { thumbnailDataUrl: photo.thumbnailDataUrl } : {}),
  }
}

export function useUploadStagePhoto(id: string) {
  const api = useApi()
  const upload = useUploader()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      stage,
      photo,
      source,
    }: {
      stage: Stage
      photo: PreparedPhoto
      source: 'desktop' | 'gallery'
    }) =>
      uploadInTwoSteps(
        photo,
        upload,
        () =>
          api.post<UploadTicket>(`/treatment-plans/${id}/photos`, {
            stage,
            mimeType: photo.mimeType,
            sizeBytes: photo.blob.size,
          }),
        (ticket) =>
          api.post<StagePhotoConfirmed>(`/treatment-plans/${id}/photos/${ticket.id}/confirm`, {
            source,
            ...confirmBody(photo, ticket),
          }),
      ),
    onSuccess: (confirmed) => {
      const { treatmentPlan, ...photo } = confirmed
      patchPlanInCache(queryClient, id, (plan) => withStagePhoto(plan, photo, treatmentPlan))
      void queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.lists })
    },
  })
}

export function useUploadLotPhoto(id: string) {
  const api = useApi()
  const upload = useUploader()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      productId,
      slot,
      photo,
      source,
    }: {
      productId: string
      slot: LotSlot
      photo: PreparedPhoto
      source: 'desktop' | 'gallery'
    }) =>
      uploadInTwoSteps(
        photo,
        upload,
        () =>
          api.post<UploadTicket>(`/treatment-plans/${id}/products/${productId}/photos`, {
            slot,
            mimeType: photo.mimeType,
            sizeBytes: photo.blob.size,
          }),
        (ticket) =>
          api.post<LotPhotoConfirmed>(
            `/treatment-plans/${id}/products/${productId}/photos/${ticket.id}/confirm`,
            { source, ...confirmBody(photo, ticket) },
          ),
      ),
    onSuccess: (confirmed, { productId, slot }) => {
      const { product, treatmentPlan, ...photo } = confirmed
      patchPlanInCache(queryClient, id, (plan) =>
        withLotPhoto(plan, productId, slot, photo, product, treatmentPlan),
      )
      void queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.lists })
    },
  })
}

export function useArchiveStagePhoto(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ stage, photoId }: { stage: Stage; photoId: string }) => {
      await api.delete(`/treatment-plans/${id}/photos/${photoId}`)
      return { stage, photoId }
    },
    onSuccess: ({ stage, photoId }) => {
      patchPlanInCache(queryClient, id, (plan) => withoutStagePhoto(plan, stage, photoId))
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useRestoreStagePhoto(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (photoId: string) =>
      api.post<Photo>(`/treatment-plans/${id}/photos/${photoId}/restore`, {}),
    onSuccess: (photo) => {
      patchPlanInCache(queryClient, id, (plan) => withStagePhoto(plan, photo))
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useArchiveLotPhoto(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      productId,
      slot,
      photoId,
    }: {
      productId: string
      slot: LotSlot
      photoId: string
    }) => {
      await api.delete(`/treatment-plans/${id}/products/${productId}/photos/${photoId}`)
      return { productId, slot }
    },
    onSuccess: ({ productId, slot }) => {
      patchPlanInCache(queryClient, id, (plan) => withLotPhoto(plan, productId, slot, null))
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

export function useRestoreLotPhoto(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, photoId }: { productId: string; photoId: string }) =>
      api.post<Photo>(`/treatment-plans/${id}/products/${productId}/photos/${photoId}/restore`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treatmentPlanKeys.detail(id) })
      return invalidateTreatmentPlans(queryClient)
    },
  })
}

// Sessão de fotos por QR (RN-PLA-20 a 23) --------------------------------------------------------

export function useOpenPhotoSession(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      target: { target: 'stage'; stage: Stage } | { target: 'product'; productId: string },
    ) => api.post<PhotoSession>(`/treatment-plans/${id}/qr`, target),
    onSuccess: (session) => {
      patchPlanInCache(queryClient, id, (plan) => ({
        ...plan,
        photoSessions: [
          ...plan.photoSessions.filter(
            (s) =>
              !(
                s.target === session.target &&
                s.stage === session.stage &&
                s.productId === session.productId
              ),
          ),
          { ...session, url: undefined },
        ],
      }))
    },
  })
}

export function useRevokePhotoSession(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (photoSessionId: string) =>
      api.delete(`/treatment-plans/${id}/qr/${photoSessionId}`),
    onSuccess: (_, photoSessionId) => {
      patchPlanInCache(queryClient, id, (plan) => ({
        ...plan,
        photoSessions: plan.photoSessions.filter((s) => s.id !== photoSessionId),
      }))
    },
  })
}

/** O token que vai na URL do QR (`/photos?t=…`), para a tela do computador consultar o estado. */
export function tokenOf(url: string): string | null {
  try {
    return new URL(url).searchParams.get('t')
  } catch {
    return null
  }
}

/**
 * Os tempos da sessão de fotos: o intervalo do polling (3 s, P-PLA-09) e as esperas entre as
 * tentativas de envio no celular (1 s, 2 s, 4 s). Testes encurtam pelo provedor.
 */
export interface PhotoTiming {
  pollIntervalMs: number
  retryDelaysMs: readonly number[]
}

export const PhotoTimingContext = createContext<PhotoTiming>({
  pollIntervalMs: 3000,
  retryDelaysMs: [1000, 2000, 4000],
})

export function usePhotoTiming() {
  return useContext(PhotoTimingContext)
}

/** A consulta do estado da sessão, com `version` para a API responder 304 quando nada mudou. */
export function usePhotoSessionStateReader() {
  const api = useApi()
  return (token: string, version: number | null) =>
    api.getConditional<PhotoSessionState>('/photos/state', {
      t: token,
      version: version ?? undefined,
    })
}

// A página pública do celular (docs/api/fotos.md) ------------------------------------------------

function withToken(path: string, token: string) {
  return `${path}?t=${encodeURIComponent(token)}`
}

export function usePublicPhotoSession(token: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: [PUBLIC_QUERY_SCOPE, 'photo-session', token ?? ''],
    queryFn: () => api.get<PhotoSessionInfo>('/photos', { t: token }),
    enabled: Boolean(token),
    retry: false,
  })
}

/** O envio pelo celular: reserva, `PUT` e confirmação, com nova tentativa sozinha quando a rede cai. */
export function usePublicPhotoUpload(token: string) {
  const api = useApi()
  const upload = useUploader()
  const timing = usePhotoTiming()
  return useMutation({
    mutationFn: ({ photo, slot }: { photo: PreparedPhoto; slot?: LotSlot }) =>
      withRetry(
        () =>
          uploadInTwoSteps(
            photo,
            upload,
            async () => {
              const ticket = await api.post<PublicUploadTicket>(
                withToken('/photos/upload', token),
                {
                  ...(slot ? { slot } : {}),
                  mimeType: photo.mimeType,
                  sizeBytes: photo.blob.size,
                },
              )
              return { id: ticket.photoId, upload: ticket.upload, storagePath: ticket.storagePath }
            },
            (ticket) =>
              api.post<PublicPhotoConfirmed>(
                withToken(`/photos/upload/${ticket.id}/confirm`, token),
                confirmBody(photo, ticket),
              ),
          ),
        timing.retryDelaysMs,
        isNetworkFailure,
      ),
  })
}

/** Só a queda de rede merece nova tentativa sozinha; recusa da API é causa e caminho na tela. */
export function isNetworkFailure(error: unknown) {
  return (
    (isApiError(error) && error.code === 'network_error') ||
    (isUploadError(error) && error.code === 'network_error')
  )
}

/** Tenta de novo depois de cada espera de `delays` enquanto `shouldRetry` disser que vale. */
export async function withRetry<T>(
  run: () => Promise<T>,
  delays: readonly number[],
  shouldRetry: (error: unknown) => boolean,
): Promise<T> {
  let attempt = 0
  for (;;) {
    try {
      return await run()
    } catch (error) {
      const delay = delays[attempt]
      if (delay === undefined || !shouldRetry(error)) throw error
      attempt += 1
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }
}
